// Who is behind a tier and behind the writing of a task, read from the event
// log — because neither table records it.
//
// `tiers` and `tasks` have no `created_by`. What Core does write, inside the
// same transaction as the change, is an event with the actor: `user_id` (the
// person) and `token_id` (the agent acting for them, when there is one). So
// authorship is answered by the event that made the thing, and by the last one
// that changed it.
//
//   tier   created  → `tiers.created`             (tier_id)
//          edited   → `tiers.draft_updated`       (tier_id), newest
//          agreed   → `tiers.agreed`              (tier_id), newest
//   task   written  → `tasks.created`             (task_id)  — created directly
//                   or `tasks.proposed`           (tier_id)  — a BATCH: the event
//                                                  names its tasks in data.task_ids
//          edited   → `tasks.proposal_updated` or `tasks.updated` (task_id), newest
//          authorized → `task.authorized`         (task_id), newest
//
// A plan does not need this: `created_by`, the version's `author_id` and the
// approval's `approved_by` are columns, read by the plan screen directly.
//
// Pure functions: tested without a browser.

export const TIER_KINDS = {
    created: 'tiers.created',
    edited: 'tiers.draft_updated',
    agreed: 'tiers.agreed',
};

export const TASK_KINDS = {
    created: 'tasks.created',
    proposed: 'tasks.proposed',
    edited: ['tasks.proposal_updated', 'tasks.updated'],
    authorized: 'task.authorized',
};

// An event row, down to what authorship needs. `null` in, `null` out.
export function actorOf(event) {
    if (!event) return null;
    return {
        eventId: event.id ?? null,
        userId: event.user_id ?? null,
        tokenId: event.token_id ?? null,
        at: event.createdAt ?? event.created_at ?? null,
    };
}

const newest = events => [...events].sort((a, b) => (b.id ?? 0) - (a.id ?? 0))[0] ?? null;

// The batch proposal that contains this task. Batches name their tasks in
// `data.task_ids`; ids compare as strings so "7" and 7 are the same task.
export function proposalOf(events, taskId) {
    const id = String(taskId);
    return (events ?? []).find(event => (
        Array.isArray(event?.data?.task_ids) && event.data.task_ids.some(item => String(item) === id)
    )) ?? null;
}

export function readTierAuthorship({ created = [], edited = [], agreed = [] } = {}) {
    return {
        created: actorOf(created[0] ?? null),
        edited: actorOf(newest(edited)),
        agreed: actorOf(newest(agreed)),
    };
}

export function readTaskAuthorship({ created = [], proposed = [], edited = [], authorized = [] } = {}, taskId) {
    const written = created[0] ?? proposalOf(proposed, taskId);
    return {
        written: actorOf(written),
        // Proposed in a batch or created directly: said, because a batch comes
        // from a master agent breaking a tier down and a direct one does not.
        writtenHow: created[0] ? 'created' : written ? 'proposed' : null,
        edited: actorOf(newest(edited)),
        authorized: actorOf(newest(authorized)),
    };
}
